import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from './Header';
import styles from './PingZeRegulatedCouplet.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

function PingZeRegulatedCouplet() {
  const [isFiveWordCouplet, setIsFiveWordCouplet] = useState(true);
  const [isZeStart, setIsZeStart] = useState(true);
  const [coupletUnits, setCoupletUnits] = useState(Array(10).fill(''));
  const [submittedCouplet, setSubmittedCouplet] = useState('');

  const [placeholderText, setPlaceholderText] = useState('');
  const placeholderContent = 'Type here...';

  const [pingZeFeedback, setPingZeFeedback] = useState('');
  const [modelAssessment, setModelAssessment] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isValid, setIsValid] = useState(null);

  useEffect(() => {
    if (!coupletUnits[0]) {
      let index = 0;
      const interval = setInterval(() => {
        setPlaceholderText(placeholderContent.substring(0, index));
        index = (index + 1) % (placeholderContent.length + 1);
      }, 200);
      return () => clearInterval(interval);
    }
  }, [coupletUnits]);

  const handleToggle = () => {
    setIsFiveWordCouplet((prev) => !prev);
    setCoupletUnits(Array(!isFiveWordCouplet ? 10 : 14).fill(''));
  };

  const handleInputChange = (index, value) => {
    const next = [...coupletUnits];
    next[index] = value;
    setCoupletUnits(next);
  };

  const formatCoupletFromUnits = () => {
    const line1 = coupletUnits.slice(0, isFiveWordCouplet ? 5 : 7).join(' ').trim();
    const line2 = coupletUnits.slice(isFiveWordCouplet ? 5 : 7).join(' ').trim();
    return `${line1}\n${line2}`.trim();
  };

  const renderCoupletInputs = () => {
    const lineUnits = isFiveWordCouplet ? 5 : 7;
    const totalLines = 2;

    const fiveWordZeStart = [
      '○/●', '○', '○/●', '●', '○',
      '○/●', '●', '○/●', '○', '○'
    ];

    const fiveWordPingStart = [
      '○/●', '●', '○/●', '○', '○',
      '○/●', '○', '○/●', '●', '○'
    ];

    const sevenWordZeStart = [
      '○/●', '○', '○/●', '●', '○/●', '○', '●',
      '○/●', '●', '○/●', '○', '○/●', '●', '●'
    ];

    const sevenWordPingStart = [
      '○/●', '●', '○/●', '○', '○/●', '●', '○',
      '○/●', '○', '○/●', '●', '○/●', '○', '○'
    ];

    const patternArray = isFiveWordCouplet
      ? (isZeStart ? fiveWordZeStart : fiveWordPingStart)
      : (isZeStart ? sevenWordZeStart : sevenWordPingStart);

    const lineBreaks = [];

    for (let i = 0; i < totalLines; i++) {
      const units = [];
      for (let j = 0; j < lineUnits; j++) {
        const index = i * lineUnits + j;

        units.push(
          <div key={`unit-${i}-${j}`} className={styles.unitContainer}>
            <div className={styles.symbolWrapper}>
              {patternArray[index]?.includes('/') ? (
                <>
                  <div className={styles.toneCircleRow}>
                    {patternArray[index].split('/').map((tone, k) => (
                      <div
                        key={k}
                        className={`${styles.toneCircle} ${tone === '●' ? styles.ze : styles.ping}`}
                      />
                    ))}
                  </div>
                  <div className={styles.toneLabel}>Ping / Ze</div>
                </>
              ) : (
                <>
                  <div
                    className={`${styles.toneCircle} ${patternArray[index] === '●' ? styles.ze : styles.ping}`}
                  />
                  <div className={styles.toneLabel}>
                    {patternArray[index] === '●' ? 'Ze' : 'Ping'}
                  </div>
                </>
              )}
            </div>

            <input
              type="text"
              value={coupletUnits[index] || ''}
              onChange={(e) => handleInputChange(index, e.target.value)}
              className={styles.unitBox}
              placeholder={index === 0 ? placeholderText : ''}
              onFocus={() => index === 0 && setPlaceholderText('')}
            />
          </div>
        );

        if (isFiveWordCouplet && j === 1) {
          units.push(<div key={`gap-${i}-${j}`} className={styles.gap} />);
        } else if (!isFiveWordCouplet && (j === 1 || j === 3)) {
          units.push(<div key={`gap-${i}-${j}`} className={styles.gap} />);
        }
      }

      lineBreaks.push(
        <div key={`line-${i}`} className={styles.line}>
          {units}
        </div>
      );
    }

    return <div className={styles.unitInputs}>{lineBreaks}</div>;
  };

  const handleSubmit = async () => {
    const coupletText = submittedCouplet.trim() || formatCoupletFromUnits();

    if (!coupletText) {
      setError('Please enter your couplet before submitting.');
      return;
    }

    setLoading(true);
    setPingZeFeedback('');
    setModelAssessment('');
    setError(null);
    setIsValid(null);

    const endpoint = '/assess-couplet';
    const payload = {
      couplet: coupletText,
      user_level: 'guest',
      level: 'juren',
    };

    try {
      const response = await axios.post(`${API_URL}${endpoint}`, payload, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });

      setIsValid(response.data.is_valid);
      setPingZeFeedback(response.data.ping_ze_feedback || '');
      setModelAssessment(response.data.model_assessment || '');
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred while processing your submission.');
    }

    setLoading(false);
  };

  const handleClear = () => {
    setSubmittedCouplet('');
    setIsFiveWordCouplet(true);
    setIsZeStart(true);
    setCoupletUnits(Array(10).fill(''));
    setPingZeFeedback('');
    setError(null);
    setLoading(false);
    setIsValid(null);
  };

  return (
    <div>
      <Header />

      <div className={styles.pageOffset}>
        <div className={styles.container}>
          <div className={styles.submissionContainer}>
            <h2>Regulated Couplet (Ping/Ze)</h2>

            <div className={styles.toggleGroup}>
              <div className={styles.toggleRow}>
                <label className={styles.toggleLabel}>
                  {isFiveWordCouplet ? '5-word couplet' : '7-word couplet'}
                </label>
                <label className={styles.switch}>
                  <input type="checkbox" checked={!isFiveWordCouplet} onChange={handleToggle} />
                  <span className={`${styles.slider} ${styles.round}`} />
                </label>
              </div>

              <div className={styles.toggleRow}>
                <label className={styles.toggleLabel}>{isZeStart ? 'Ze Start' : 'Ping Start'}</label>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={!isZeStart}
                    onChange={() => setIsZeStart((prev) => !prev)}
                  />
                  <span className={`${styles.slider} ${styles.round}`} />
                </label>
              </div>
            </div>

            <p>Type in unit by unit:</p>
            {renderCoupletInputs()}

            <p>OR</p>
            <textarea
              placeholder={
                "Enter your couplet here...\n\nNote: Please remove all punctuation from your couplet including apostrophes!"
              }
              value={submittedCouplet}
              onChange={(e) => setSubmittedCouplet(e.target.value)}
              className={styles.textArea}
            />

            <div className={styles.submitButtonsContainer}>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`${styles.button} ${styles.aiButton}`}
              >
                {loading ? 'Checking...' : 'Submit'}
              </button>

              <button
                onClick={handleClear}
                disabled={loading}
                className={`${styles.button}`}
              >
                Clear
              </button>
            </div>

            {loading && <div className={styles.loader} />}
            {error && <div className={styles.errorMessage}>{error}</div>}

            {pingZeFeedback && (
              <div className={styles.pingZeFeedback}>
                <h4>Ping/Ze Feedback</h4>
                {pingZeFeedback.split('\n').map((line, idx) => (
                  <p key={idx}>{line}</p>
                ))}
              </div>
            )}
            {modelAssessment && isValid && (
              <div className={styles.pingZeFeedback}>
                <h4>Model Assessment</h4>
                <p>{modelAssessment}</p>
              </div>
            )}

            {/* If you ever want to show validity on UI later, we already store isValid */}
            {/* {isValid === false && <div className={styles.errorMessage}>Invalid submission.</div>} */}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PingZeRegulatedCouplet;
